import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import { getSynonymSearchApiKey } from 'api/getSynonymSearchApiKey';
import { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { FieldDescription } from 'components/Form/Field/FieldDescription';
import { FieldLabel } from 'components/Form/Field/FieldLabel';
import { Form, FormUsageContext } from 'components/Form/Form';
import { Icon } from 'components/Icon/Icon';
import { InputIconAffix } from 'components/Input/affixes/InputIconAffix';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { salariesUpdateCareerPathSynonyms } from 'modules/salaries/api/salariesUpdateCareerPathSynonyms';
import { CareerPathAnalyticsData } from 'modules/salaries/types/CareerPathAnalyticsData';
import { EstimatedRange } from 'modules/salaries/types/EstimatedRange';
import { SalariesJobSynonym } from 'modules/salaries/types/SalariesJobSynonym';
import { searchAlgoliaConfig } from 'modules/search/algolia/searchAlgoliaConfig';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';

import {
  AddTitlesIconContainer,
  ButtonContainer,
  CareerPathFormContainer,
  CareerPathFormText,
  Container,
  IconContainer,
  JobTitleFieldContainer,
  StyledAutocompleteTextInputField,
  StyledBorderlessButton,
  StyledPrimaryButton,
} from './CareerPathForm.styled';

type CareerPathFormProps = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  analyticsData: CareerPathAnalyticsData;
  careerPathData: EstimatedRange[];
  salaryModelCreated: ApiISODateTimeString;
  setCareerPathData: (careerPath: EstimatedRange[]) => void;
  city?: string;
  selectedSynonymId: string;
  stateCode?: string;
  searchApiKey: string;
  setSearchApiKey: (arg0: string) => void;
  yearsOfExperience?: number;
};

export function CareerPathForm({
  analyticsTitle,
  usageContext,
  analyticsData,
  careerPathData,
  setCareerPathData,
  salaryModelCreated,
  selectedSynonymId,
  city,
  stateCode,
  yearsOfExperience,
  searchApiKey,
  setSearchApiKey,
}: CareerPathFormProps) {
  const [initialized, setInitialized] = useState(false);
  const [transitioned, setTransitioned] = useState(false);
  useEffect(() => {
    setInitialized(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  useEffect(() => {
    setTransitioned(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  const initializeCareerPathForm = () => {
    setInitialized(true);
    trackClicked('Career Path Add Title', analyticsData);
  };

  const initialValues = {};
  const onTransitionEnd = ({ propertyName }: { propertyName: string }) => {
    if (propertyName === 'clip-path') {
      setTransitioned(true);
    }
  };
  const onSubmit = async ({
    handleError,
    updateValues,
    updateSubmitting,
    values,
  }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any) => {
    if (!values.additionalSynonymId) {
      updateSubmitting(false);
      return;
    }

    const updatedSynonymIds = [
      ...careerPathData
        .filter((cp) => cp.synonymId !== selectedSynonymId)
        .map((cp) => cp.synonymId),
      values.additionalSynonymId,
    ];

    const careerPath = await salariesUpdateCareerPathSynonyms({
      selectedSynonymId,
      city,
      stateCode,
      yearsOfExperience,
      currentSynonymIds: updatedSynonymIds,
      synonymIdToAdd: values.additionalSynonymId,
    });
    // Mimic form error when a null career path value exists
    if (careerPath.find((career) => career.salary === null)) {
      const error = {
        responseJson: {
          errors: [
            {
              type: 'not_enough_data',
              name: 'synonymId',
              description: ' ', // @HACK: Blank space keeps error as truthy w/o displaying text
            },
          ],
        },
      };
      handleError(error);
      updateSubmitting(false);
    } else {
      setCareerPathData(careerPath);
      updateValues({
        additionalSynonymName: '',
        additionalSynonymId: '',
        additionalSynonymStandardName: '',
        synonymId: '',
      });
      updateSubmitting(false);
      trackEvent('Career Path Success Added Title', {
        ...analyticsData,
        job_titles_displayed: careerPath.map((estimatedRange) => ({
          synonym_name: estimatedRange.synonymName,
          standard_name: estimatedRange.jobTitleName,
        })),
        job_title_added: {
          synonym_name: values.additionalSynonymName,
          standard_name: values.additionalSynonymStandardName,
        },
      });
    }
  };

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      data-qa-id="career-path-chart-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({
        errors,
        clearErrors,
        submitting,
        updateValues,
        submitForm,
      }) => (
        <Container>
          <CareerPathFormContainer
            $hasError={Boolean(errors.synonymId)}
            $initialized={initialized}
            $transitioned={transitioned}
            onTransitionEnd={onTransitionEnd}
          >
            <CareerPathFormText>
              <FieldLabel>
                {errors.synonymId
                  ? getText('Oops! Try another title')
                  : getText('Add your own titles')}
              </FieldLabel>
              <FieldDescription>
                {errors.synonymId
                  ? getText(
                      "We don't currently have enough data to add this title to your career path.",
                    )
                  : getText('Start typing to see suggestions')}
              </FieldDescription>
            </CareerPathFormText>
            <JobTitleFieldContainer>
              <IconContainer>
                <InputIconAffix iconName="search" noPaddingRight />
              </IconContainer>
              <StyledAutocompleteTextInputField
                filterOptions={`NOT ${careerPathData
                  .map((career) => `jobTitle.id:${career.jobTitleId}`)
                  .join(
                    ' AND NOT ',
                  )} AND includeInSurvey: true AND jobTitle.created < ${salaryModelCreated}`}
                height="35px"
                noMargin
                name="synonymId"
                autocompleteType="SALARY_REPORT_SURVEY_TITLES"
                onSuggestionSelected={(
                  _,
                  { suggestion }: { suggestion: { value: SalariesJobSynonym } },
                ) => {
                  const { value } = suggestion;
                  flushSync(() => {
                    updateValues({
                      additionalSynonymId: value.objectID,
                      additionalSynonymName: value.name,
                      additionalSynonymStandardName: value.jobTitle.name,
                    });
                  });
                  clearErrors(['selectedSynonymId']);
                  submitForm();
                }}
                searchConfig={searchAlgoliaConfig(searchApiKey)}
                updateApiKey={() =>
                  getSynonymSearchApiKey().then((newApiKey) => {
                    setSearchApiKey(newApiKey);
                  })
                }
              />
              <ButtonContainer>
                {submitting ? (
                  <LoadingDots size="small" />
                ) : (
                  <StyledBorderlessButton
                    type="submit"
                    data-qa-id="career-path-chart-form-submit-button"
                  >
                    + Add Title
                  </StyledBorderlessButton>
                )}
              </ButtonContainer>
            </JobTitleFieldContainer>
          </CareerPathFormContainer>
          <StyledPrimaryButton
            data-qa-id="career-path-chart-form-button"
            $initialized={initialized}
            $transitioned={transitioned}
            onClick={() => initializeCareerPathForm()}
          >
            <AddTitlesIconContainer>
              {getText('Add Titles')}
              <Icon name="plus" size={16} />
            </AddTitlesIconContainer>
          </StyledPrimaryButton>
        </Container>
      )}
    />
  );
}
